// ABOUTME: Main SharePoint connector form field component using extracted components and hooks
// ABOUTME: Integrates with existing form field architecture and provides file browsing functionality
/* eslint-disable react/prop-types */

import { Box, Typography } from '@ds/coolshop';
import { FieldProps } from '@src/assets/types/hierarchies/Hierarchies.types';
import { EditNodeContext } from '@src/store/hierarchy/EditNodeContext';
import React, { useContext } from 'react';

import { ActionButtons } from './components/ActionButtons';
import { EmptyFileDisplay } from './components/EmptyFileDisplay';
import { SelectedFileDisplay } from './components/SelectedFileDisplay';
import { useFileFormatting } from './hooks/useFileFormatting';
import { useFileSelection } from './hooks/useFileSelection';
import { SharePointFileBrowserModal } from './SharePointFileBrowserModal';

type ISharePointConnectorFieldProps = Readonly<
    Omit<FieldProps['sharepoint_connector'], 'hierarchyId' | 'nodeId'>
>;

export const SharePointConnectorField: React.FC<ISharePointConnectorFieldProps> = React.memo(
    ({ name, label, control, readOnly = false, disabled = false, required = false }) => {
        // Get hierarchyId from context
        const { state: editNodeState } = useContext(EditNodeContext);
        const hierarchyId = editNodeState?.hierarchyId;

        // Custom hooks for file selection and formatting
        const {
            selectedFile,
            isModalOpen,
            handleFileSelect,
            handleClearSelection,
            handleOpenModal,
            handleCloseModal,
        } = useFileSelection({
            name,
            control,
            hierarchyId,
            disabled,
            readOnly,
        });

        const { formatFileSize, formatDate } = useFileFormatting();

        return (
            <Box>
                {/* Field Label */}
                <Typography variant="body2" color="text.secondary" mb={1}>
                    {label}
                    {required && (
                        <Typography component="span" color="error.main" ml={0.5}>
                            *
                        </Typography>
                    )}
                </Typography>

                {/* Selected File Display or Empty State */}
                {selectedFile ? (
                    <SelectedFileDisplay
                        selectedFile={selectedFile}
                        formatFileSize={formatFileSize}
                        formatDate={formatDate}
                    />
                ) : (
                    <EmptyFileDisplay />
                )}

                {/* Action Buttons */}
                <ActionButtons
                    selectedFile={selectedFile}
                    onBrowse={handleOpenModal}
                    onClear={handleClearSelection}
                    disabled={disabled}
                    readOnly={readOnly}
                />

                {/* SharePoint File Browser Modal */}
                {hierarchyId && (
                    <SharePointFileBrowserModal
                        open={isModalOpen}
                        onClose={handleCloseModal}
                        onSelect={handleFileSelect}
                        hierarchyId={hierarchyId}
                        initialFile={selectedFile}
                    />
                )}
            </Box>
        );
    },
);

SharePointConnectorField.displayName = 'SharePointConnectorField';
